"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import CircleLoader from "@/components/distribution-plan-tool/common/CircleLoader";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { WaveGroupType } from "../WaveGroup.types";
import { useWaveGroupEditButtonsController, WaveGroupIdentitiesModal } from "./buttons/hooks/useWaveGroupEditButtonsController";
import WaveGroupEditMenu from "./buttons/subcomponents/WaveGroupEditMenu";
import WaveGroupManageIdentitiesModals from "./buttons/subcomponents/WaveGroupManageIdentitiesModals";
import { WaveGroupManageIdentitiesMode, type WaveGroupManageIdentitiesConfirmEvent } from "./WaveGroupManageIdentitiesModal";
import WaveGroupChangeDialog from "./WaveGroupChangeDialog";
import type { ApiCreateGroup } from "@/generated/models/ApiCreateGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { useGroupMutations } from "@/hooks/groups/useGroupMutations";
import { buildWaveUpdateBody, getScopedGroup } from "./buttons/utils/waveGroupEdit";
interface WaveGroupEditButtonsProps {
  readonly haveGroup: boolean;
  readonly wave: ApiWave;
  readonly type: WaveGroupType;
}
export default function WaveGroupEditButtons(t0) {
  const $ = _c(56);
  const {
    haveGroup,
    wave,
    type
  } = t0;
  const {
    setToast,
    requestAuth,
    connectedProfile
  } = useContext(AuthContext);
  const {
    onWaveCreated,
    onGroupCreate
  } = useContext(ReactQueryWrapperContext);
  const [isGroupChangeOpen, setIsGroupChangeOpen] = useState(false);
  const skipNextGroupChangeAuthRef = useRef(false);
  let t1;
  if ($[0] !== type || $[1] !== wave) {
    t1 = getScopedGroup(wave, type);
    $[0] = type;
    $[1] = wave;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const scopedGroup = t1;
  let t2;
  if ($[3] !== onGroupCreate || $[4] !== requestAuth) {
    t2 = {
      requestAuth,
      onGroupCreate
    };
    $[3] = onGroupCreate;
    $[4] = requestAuth;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    submit: submitInlineGroup
  } = useGroupMutations(t2);
  let t3;
  if ($[6] !== connectedProfile || $[7] !== haveGroup || $[8] !== onWaveCreated || $[9] !== requestAuth || $[10] !== setToast || $[11] !== type || $[12] !== wave) {
    t3 = {
      haveGroup,
      wave,
      type,
      connectedProfile,
      requestAuth,
      setToast,
      onWaveCreated
    };
    $[6] = connectedProfile;
    $[7] = haveGroup;
    $[8] = onWaveCreated;
    $[9] = requestAuth;
    $[10] = setToast;
    $[11] = type;
    $[12] = wave;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const {
    mutating,
    updateWave,
    canIncludeIdentity,
    canExcludeIdentity,
    canRemoveGroup,
    activeIdentitiesModal,
    openIdentitiesModal,
    closeIdentitiesModal,
    onIdentityConfirm
  } = useWaveGroupEditButtonsController(t3);
  let t4;
  if ($[14] !== onIdentityConfirm) {
    t4 = t5 => {
      const {
        identity,
        mode
      } = t5;
      const normalizedMode = mode === WaveGroupManageIdentitiesMode.INCLUDE ? WaveGroupIdentitiesModal.INCLUDE : WaveGroupIdentitiesModal.EXCLUDE;
      onIdentityConfirm({
        identity,
        mode: normalizedMode
      });
    };
    $[14] = onIdentityConfirm;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleIdentityConfirm = t4;
  let t5;
  if ($[16] !== openIdentitiesModal) {
    t5 = () => openIdentitiesModal(WaveGroupIdentitiesModal.INCLUDE);
    $[16] = openIdentitiesModal;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleIncludeIdentity = t5;
  let t6;
  if ($[18] !== openIdentitiesModal) {
    t6 = () => openIdentitiesModal(WaveGroupIdentitiesModal.EXCLUDE);
    $[18] = openIdentitiesModal;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleExcludeIdentity = t6;
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsGroupChangeOpen(true);
    };
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleChangeGroupOpen = t7;
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      skipNextGroupChangeAuthRef.current = false;
      setIsGroupChangeOpen(false);
    };
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleChangeGroupClose = t8;
  let t9;
  if ($[22] !== type || $[23] !== updateWave || $[24] !== wave) {
    t9 = async group => {
      if (!group) {
        return;
      }
      const skipAuth = skipNextGroupChangeAuthRef.current;
      skipNextGroupChangeAuthRef.current = false;
      try {
        await updateWave(buildWaveUpdateBody(wave, type, group.id), {
          skipAuth
        });
        setIsGroupChangeOpen(false);
      } catch {}
    };
    $[22] = type;
    $[23] = updateWave;
    $[24] = wave;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const handleGroupChange = t9;
  let t10;
  if ($[26] !== connectedProfile?.handle || $[27] !== setToast || $[28] !== submitInlineGroup) {
    t10 = async payload => {
      const result = await submitInlineGroup({
        payload,
        currentHandle: connectedProfile?.handle ?? null
      });
      if (!result.ok) {
        if (result.reason !== "auth") {
          setToast({
            message: result.error,
            type: "error"
          });
        }
        return null;
      }
      setToast({
        message: "Group created.",
        type: "success"
      });
      skipNextGroupChangeAuthRef.current = true;
      return result.group;
    };
    $[26] = connectedProfile?.handle;
    $[27] = setToast;
    $[28] = submitInlineGroup;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  connectedProfile?.handle;
  const handleInlineGroupCreate = t10;
  if (mutating) {
    let t11;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <output aria-live="polite" className="tw-inline-flex tw-items-center tw-gap-2"><CircleLoader /><span className="tw-sr-only">Updating wave group</span></output>;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    return t11;
  }
  let t11;
  if ($[31] !== canExcludeIdentity || $[32] !== canIncludeIdentity || $[33] !== canRemoveGroup || $[34] !== handleExcludeIdentity || $[35] !== handleIncludeIdentity || $[36] !== haveGroup || $[37] !== type || $[38] !== updateWave || $[39] !== wave) {
    t11 = <WaveGroupEditMenu wave={wave} type={type} onWaveUpdate={updateWave} hasGroup={haveGroup} canIncludeIdentity={canIncludeIdentity} canExcludeIdentity={canExcludeIdentity} canRemoveGroup={canRemoveGroup} onIncludeIdentity={handleIncludeIdentity} onExcludeIdentity={handleExcludeIdentity} onChangeGroup={handleChangeGroupOpen} />;
    $[31] = canExcludeIdentity;
    $[32] = canIncludeIdentity;
    $[33] = canRemoveGroup;
    $[34] = handleExcludeIdentity;
    $[35] = handleIncludeIdentity;
    $[36] = haveGroup;
    $[37] = type;
    $[38] = updateWave;
    $[39] = wave;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== handleGroupChange || $[42] !== handleInlineGroupCreate || $[43] !== isGroupChangeOpen || $[44] !== scopedGroup || $[45] !== type || $[46] !== wave) {
    t12 = isGroupChangeOpen && <WaveGroupChangeDialog wave={wave} type={type} currentGroup={scopedGroup} onClose={handleChangeGroupClose} onGroupChange={handleGroupChange} onCreateGroup={handleInlineGroupCreate} />;
    $[41] = handleGroupChange;
    $[42] = handleInlineGroupCreate;
    $[43] = isGroupChangeOpen;
    $[44] = scopedGroup;
    $[45] = type;
    $[46] = wave;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== activeIdentitiesModal || $[49] !== closeIdentitiesModal || $[50] !== handleIdentityConfirm) {
    t13 = <WaveGroupManageIdentitiesModals activeModal={activeIdentitiesModal} onClose={closeIdentitiesModal} onConfirm={handleIdentityConfirm} />;
    $[48] = activeIdentitiesModal;
    $[49] = closeIdentitiesModal;
    $[50] = handleIdentityConfirm;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== t11 || $[53] !== t12 || $[54] !== t13) {
    t14 = <>{t11}{t12}{t13}</>;
    $[52] = t11;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  return t14;
}